import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocalization } from "../hooks/useLocalization";
import { HaloAnimation } from "../components/HaloAnimation";

/* ===== Page：可捲動 ===== */
const Page = styled.div`
  position: relative;
  /* min-height: 100svh; */
  width: 100%;
  margin: 0 auto;
  padding: 0 16px;
  box-sizing: border-box;
  z-index: 10;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    width: 85%;
  }
`;

/* ===== 兩欄：左 Avatar、右訊息卡 ===== */
const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

/* 左欄：頭像（圖片） */
const Avatar = styled.div`
  flex: 0 0 56px; /* 欄寬 */
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35),
    inset 0 0 0 1px rgba(255, 255, 255, 0.12);

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    flex: 0 0 88px;
    height: 88px;
  }
`;

/* 右欄：圓角訊息卡（玻璃感） */
const Bubble = styled.div`
  flex: 1 1 auto;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(15, 27, 60, 0.45);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35),
    inset 0 0 0 1px rgba(255, 255, 255, 0.04);
  overflow: hidden;
  margin-bottom: 48px;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    border-radius: 28px;
  }
`;

const BubbleInner = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;
  padding: 14px 14px 16px;
  box-sizing: border-box;
`;

/* 標題（兩行內可換行） */
const BubbleTitle = styled.h2`
  margin: 0;
  color: #fff;
  font-family: "PingFang TC", "SF Pro TC", "Noto Sans TC", system-ui, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 26px;
  flex: 0 0 auto; /* 不參與剩餘高度分配 */

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 20px;
    line-height: 72px;
    height: 72px;
  }
`;

const Figure = styled.div`
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 0; /* 🔹避免 flex 子元素撐爆 */
  max-width: min(100%, 66.666vw);
  padding: 0 24px;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    padding: 0 100px;
  }
`;

const Img = styled.img`
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%; /* 🔹圖片高度限制在可用區域內 */
  object-fit: contain;
  border-radius: 16px;
  display: block;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.35);
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: nowrap;
  flex-shrink: 0; /* 🔹固定在底部，不被壓縮 */
  padding-bottom: 14px;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    margin-top: 26px;
    gap: 24px;
    padding: 0 16px 20px;
  }
`;

/* 左：透明 + 白字 + 白描邊 */
const GhostBtn = styled.button`
  width: 100%;
  flex-grow: 1;
  appearance: none;
  border-radius: 9999px;
  padding: 12px 22px;
  border: 1px solid rgba(255, 255, 255, 0.9);
  background: transparent;
  color: #fff;
  font-weight: 600;
  letter-spacing: 0.5px;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 24px;
    padding: 18px 40px;
  }
`;

/* 右：白底 + 黑字 */
const PrimaryBtn = styled.button`
  width: 100%;
  flex-grow: 1;
  appearance: none;
  border-radius: 9999px;
  padding: 12px 28px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: #ffffff;
  color: #0b1226;
  font-weight: 700;
  letter-spacing: 0.5px;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 24px;
    padding: 18px 40px;
  }
`;

export default function PreviewImagePage() {
  const nav = useNavigate();
  const { t } = useLocalization();
  const { state } = useLocation() as { state?: { dataUrl?: string } };
  const dataUrl = state?.dataUrl || "";

  if (!dataUrl) {
    return (
      <Page>
        <GhostBtn onClick={() => nav(-1)}>{t("back")}</GhostBtn>
      </Page>
    );
  }

  return (
    <Page>
      <Row>
        {/* 左欄：Avatar */}
        <Avatar>
          <HaloAnimation />
        </Avatar>

        {/* 右欄：訊息卡 */}
        <Bubble>
          <BubbleInner>
            <BubbleTitle>{t("confirm_image_prompt")}</BubbleTitle>

            <Figure>
              <Img src={dataUrl} alt={t("preview_image_alt")} />
            </Figure>

            <ActionsRow>
              <GhostBtn onClick={() => nav(-1)}>{t("re_upload")}</GhostBtn>
              <PrimaryBtn
                onClick={() =>
                  nav("/analyzing", { state: { mode: "image", dataUrl } })
                }
              >
                {t("submit")}
              </PrimaryBtn>
            </ActionsRow>
          </BubbleInner>
        </Bubble>
      </Row>
    </Page>
  );
}
